import type { Chat, ChatStatus, TaskSource, Worktree, WorktreeStatus } from "@rivets/core";

/**
 * Why a task has no checkout on its worker yet, or null once it has one.
 *
 * `awaiting_approval` is integration-sourced work nobody has approved: the row
 * exists so the task can be seen and decided on, but no worker has been asked
 * to create anything. Everything that needs the checkout — terminals, scripts,
 * files, Git and pull-request actions, previews — treats it exactly like
 * `provisioning`, and only the words differ.
 */
export type PendingCheckout = "awaiting_approval" | "provisioning";

export function pendingCheckout(status: WorktreeStatus | undefined): PendingCheckout | null {
  switch (status) {
    case "awaiting_approval":
    case "provisioning":
      return status;
    case "ready":
    case "error":
    case "archived":
    case undefined:
      return null;
  }
}

/** The headline a surface shows in place of what needs the checkout. */
export function pendingCheckoutTitle(pending: PendingCheckout): string {
  return pending === "awaiting_approval" ? "Waiting for approval" : "Setting up workspace";
}

/** The one-line reason beside a disabled control. */
export function pendingCheckoutReason(pending: PendingCheckout): string {
  return pending === "awaiting_approval" ? "Waiting for approval" : "Workspace is being prepared";
}

/** A chat's status in words, for tab tooltips and labels. */
export function chatStatusLabel(status: ChatStatus): string {
  switch (status) {
    case "awaiting_approval": return "Awaiting approval";
    case "idle": return "Idle";
    case "queued": return "Queued";
    case "running": return "Running";
    case "failed": return "Failed";
    case "archived": return "Archived";
  }
}

/** The task, or any of its chats, is waiting on a member's approval. */
export function taskAwaitingApproval(worktree: Pick<Worktree, "status"> | undefined, chats: readonly Pick<Chat, "status">[]): boolean {
  return worktree?.status === "awaiting_approval" || chats.some((chat) => chat.status === "awaiting_approval");
}

/** Work that started from text written outside the organization. */
export function isIntegrationSource(source: TaskSource | null | undefined): boolean {
  return source?.kind === "linear" || source?.kind === "slack";
}

/**
 * Whether the task view should look up the chat's approval: always while it
 * is waiting, and for the first chat of an integration-sourced task, so the
 * record of who approved it stays on the page after the run starts.
 */
export function chatNeedsApprovalLookup(
  chat: Pick<Chat, "id" | "status"> | undefined,
  worktree: Pick<Worktree, "source"> | undefined,
  chats: readonly Pick<Chat, "id" | "createdAt">[],
): boolean {
  if (!chat) return false;
  if (chat.status === "awaiting_approval") return true;
  if (!isIntegrationSource(worktree?.source)) return false;
  const first = chats.reduce<Pick<Chat, "id" | "createdAt"> | undefined>(
    (earliest, candidate) => !earliest || candidate.createdAt < earliest.createdAt ? candidate : earliest,
    undefined,
  );
  return first?.id === chat.id;
}

/** The one mark a sidebar row carries, most urgent first: a fault, then work
 * waiting for someone to approve it, then work in flight. */
export function worktreeActivity(
  worktree: Pick<Worktree, "status">,
  chats: readonly Pick<Chat, "status">[],
): { kind: "failed" | "awaiting" | "running" | "queued"; title: string } | null {
  if (chats.some((chat) => chat.status === "failed") || worktree.status === "error") return { kind: "failed", title: "A chat failed" };
  if (taskAwaitingApproval(worktree, chats)) return { kind: "awaiting", title: "Waiting for approval" };
  if (chats.some((chat) => chat.status === "running")) return { kind: "running", title: "Agent running" };
  if (chats.some((chat) => chat.status === "queued")) return { kind: "queued", title: "Agent queued" };
  return null;
}
